// src/App.js
import { React, useEffect, useState, forwardRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import CurrencyExchangeRoundedIcon from '@mui/icons-material/CurrencyExchangeRounded';
import AssuredWorkloadRoundedIcon from '@mui/icons-material/AssuredWorkloadRounded';
import { EventAvailable, EventBusy, PermContactCalendar, PublishedWithChangesOutlined, SportsBasketball, Summarize } from '@mui/icons-material';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import PlayCircleFilledIcon from '@mui/icons-material/PlayCircleFilled';
import FeedbackIcon from '@mui/icons-material/Feedback';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import EventIcon from '@mui/icons-material/Event';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import Tooltip from '@mui/material/Tooltip';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/CloseRounded';
import Slide from '@mui/material/Slide';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { socket } from '../services/socket';
import { Outlet, Route, Routes } from 'react-router-dom';
import { mainRoutes } from '../config/routes.config';
import { useRouteUtils } from '../hooks/useRouteUtils.ts';
import { getMaestroAuth } from '../maestro/maestro';
import Users from '../components/users';
import Commissions from '../components/commissions';
import CommissionsApproval from '../components/CommissionsApproval';
import VideoPlayer from '../components/videoplayer';
import FeatureRequest from '../components/FeatureRequest';
import EventRequest from '../components/EventRequest';
import Vendors from '../components/vendors';
import CycleManagement from '../components/CycleManagement';
import CloseCycles from '../components/CloseCycles';
import InvoiceReport from '../components/InvoiceReport';

var api = axios.create({ withCredentials: true });

const Transition = forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

function Landing() {
  const { 
    location, 
    dynamicModalRouteLabel, 
    matches,
    navigate, 
    handleModalNavigate,
    handleTabChange
  } = useRouteUtils();

  const { isLoading, isAuthenticated, error, user, getAccessTokenSilently, loginWithRedirect, logout } = useAuth0();
  const [isConnected, setIsConnected] = useState(socket.connected);
  const [current_user, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(0);
  const [customer_list, setCustomerList] = useState([]);
  const [current_broker, setCurrentBroker] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [openDialog, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState(null);
  const [hasTenant, setHasTenant] = useState(false);
  const [checkingTenant, setCheckingTenant] = useState(false);
  const [openOptInDialog, setOpenOptInDialog] = useState(false);
  const [tipaltiQueryString, setTipaltiQueryString] = useState(null);
  const [maestroPermissions, setMaestroPermissions] = useState([]);

  const tenant = process.env.REACT_APP_TENANT;
  const currentRoute = matches?.[matches.length - 1]?.route;
  const isCurrentlyModal = currentRoute?.isModal;

  const activeRouteTabValue = isCurrentlyModal 
  ? (location.state?.background?.pathname || '/dashboard') 
  : (location.pathname === '/' ? '/dashboard' : location.pathname);

  const backgroundTabLocation = { ...location, pathname: activeRouteTabValue };

  const handleDialogClickOpen = (content) => {
    setDialogContent(content);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setDialogContent(null);

    if (location.state?.background) {
      navigate(-1);
    } else {
      navigate('/dashboard', { replace: true });
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const openEventRequestDialog = (content) => {
    handleDialogClickOpen('event-request');
  };

  const checkTenantStatus = async () => {
    try {
      if (process.env.REACT_APP_BROKERNERD_API) {
        setCheckingTenant(true);
        const token = await getAccessTokenSilently({
          audience: process.env.REACT_APP_AUTH0AUDIENCE,
          scope: process.env.REACT_APP_AUTH0SCOPE
        });

        const response = await api.get(
          `${process.env.REACT_APP_BROKERNERD_API}/tenants`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        setHasTenant(response.data && response.data.length > 0);
        setCheckingTenant(false);
      }
    } catch (error) {
      console.error('Error checking tenant status:', error);
      setCheckingTenant(false);
    }
  };

  const handleOptInToPricingTool = () => {
    handleClose();

    // Check if current_broker is 'all'
    if (current_broker === 'all') {
      toast.error('Select A Vendor First');
      return;
    }

    // Show confirmation dialog
    setOpenOptInDialog(true);
  };

  const handleOptInDialogClose = () => {
    setOpenOptInDialog(false);
  };

  const handleConfirmOptIn = async () => {
    setOpenOptInDialog(false);

    try {
      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0AUDIENCE,
        scope: process.env.REACT_APP_AUTH0SCOPE
      });

      // Get the company name from the customer_list array matched to current_broker
      const currentCustomer = customer_list.find(customer => customer.buyer_broker_id === current_broker);
      const companyName = currentCustomer?.bought_from_name || current_broker;
      const inventoryTag = `RP_${current_broker}`;

      const response = await api.post(
        `${process.env.REACT_APP_BROKERNERD_API}/onboarding/${process.env.REACT_APP_BROKERNERD_TENANT}`,
        {
          companyName: companyName,
          inventoryTag: inventoryTag
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.status === 200) {
        toast.success('Successfully opted in to Pricing Tool!');

        // Update tenant status
        setHasTenant(true);

        // Open pricing tool
        window.open(`${process.env.REACT_APP_BROKERNERD_PORTAL}`, '_blank');
      }
    } catch (error) {
      console.error('Error opting in to pricing tool:', error);
      toast.error('Error: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleOpenPricingTool = () => {
    handleClose();
    window.open(`${process.env.REACT_APP_BROKERNERD_PORTAL}`, '_blank');
  };

  function getQueryString() {
    getAccessTokenSilently({
      audience: process.env.REACT_APP_AUTH0AUDIENCE,
      scope: process.env.REACT_APP_AUTH0SCOPE
    }).then((token) => {
      api.get(
          `${process.env.REACT_APP_BASEURL}/api/batches/tipalti-query-string/${current_broker}`,
          { headers: { Authorization: `Bearer ${token}` } }
        ).then((response) => {
          setTipaltiQueryString(response.data);
        }).catch((err) => {
          setTipaltiQueryString(null);
        });
    });
  }

  function getCurrentUser(controller) {
    getAccessTokenSilently({
      audience: process.env.REACT_APP_AUTH0AUDIENCE,
      scope: process.env.REACT_APP_AUTH0SCOPE
    }).then((token) => {
      
      api.get(
        `${process.env.REACT_APP_BASEURL}/api/users/user/${user.sub}`,
        { signal: controller.signal, headers: { Authorization: `Bearer ${token}` } }
      ).then((response) => {
        response.data.enableNcaaReport = process.env.REACT_APP_ENABLE_NCAA_REPORT === 'true' ? true : false;

        setCurrentUser(response.data);
        
        let brokerId = current_broker;
        if (brokerId) {
          if (!response.data.company.includes('all')) {
            brokerId = response.data.company[0];
          }
        } else {
          brokerId = response.data.company[0];
        }
        setCurrentBroker(brokerId);

        socket.emit('join', response.data._id);
        if (response.data.admin === true || response.data.client_admin === true) {
          socket.emit('join', 'admin');
        } else {
          socket.emit('leave', 'admin');
        }

        api.post(
          `${process.env.REACT_APP_BASEURL}/api/dashboard/customers`,
          { company: response.data.company },
          { signal: controller.signal, headers: { Authorization: `Bearer ${token}` } }
        ).then((cresponse) => {
          var tmp = [];
          cresponse.data.forEach((record) => {
            tmp.push(record._id)
          });
          setCustomerList(tmp);
        }).catch((err) => {
          if (err.message !== 'canceled') {
            console.log(err);
          }
        });
      }).catch((err) => {
        // Server-side force-logout kill switch: token was issued before the
        // configured cutoff, so drop the session and send the user to login.
        if (
          err.response &&
          err.response.status === 401 &&
          err.response.data &&
          err.response.data.code === 'force_logout'
        ) {
          logout({ logoutParams: { returnTo: window.location.origin } });
          return;
        }
        if (err.message !== 'canceled') {
          console.log(err);
        }
      });

  });
}

function optInMenu() {
  if (process.env.REACT_APP_BROKERNERD_API) {
    if (!checkingTenant && !hasTenant) {
      return (
      <>
        <MenuItem onClick={handleOptInToPricingTool}>
          <ListItemIcon>
            <PriceChangeIcon fontSize="small" color='primary' />
          </ListItemIcon>
          Opt-in to Pricing Tool
        </MenuItem>
        <Divider />
      </>
      );
    } else if(hasTenant) {
      return (
        <>
        <MenuItem onClick={handleOpenPricingTool}>
          <ListItemIcon>
            <PriceChangeIcon fontSize="small" color='primary' />
          </ListItemIcon>
          Pricing Tool
        </MenuItem>
        <Divider />
        </>
      );
    }
  }

  return null;
}

function optInConfirmationDialog() {
  if (process.env.REACT_APP_BROKERNERD_API) {
    return (
          <Dialog
            open={openOptInDialog}
            onClose={handleOptInDialogClose}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              Opt in to Smart Pricing
            </DialogTitle>
            <DialogContent>
              <Typography variant="body1" paragraph sx={{ mt: 2 }}>
                By turning on the RevPro Pricing Tool, you'll unlock data-driven insights designed to help you stay ahead of the market.
              </Typography>
              <Typography variant="body2" paragraph>
                When activated, your tagged inventory (<strong>RP_{current_broker}</strong>) securely syncs with RevPro for real-time pricing analysis and recommendations, helping you maximize revenue and outperform the competition.
              </Typography>
              <Typography variant="body2" paragraph>
                You're in control, you can adjust your preferences anytime through your account settings.
              </Typography>
              <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                Ready to launch the RevPro Pricing Tool?
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleOptInDialogClose} color="inherit">
                Cancel
              </Button>
              <Button onClick={handleConfirmOptIn} variant="contained" color="primary">
                Opt-in
              </Button>
            </DialogActions>
          </Dialog>
    );
  }

  return null;
}

  useEffect(() => {
    function onConnect() {
      setIsConnected(true);
    }

    function onDisconnect() {
      setIsConnected(false);
    }

    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);

    return () => {
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    if (isAuthenticated && isConnected) {
      getCurrentUser(controller);

      if (process.env.REACT_APP_BROKERNERD_API) {
        checkTenantStatus();
      }

      socket.on('user', () => { getCurrentUser(controller); });
    }

    return () => {
      socket.off('user');
      controller.abort();
    };
  }, [isConnected, isAuthenticated]);

  useEffect(() => {
    const controller = new AbortController();

    if (isAuthenticated && isConnected && current_broker) {
      getQueryString();
    }

    return () => {
      controller.abort();
    };
  }, [current_broker]);

  useEffect(() => {
    if (isAuthenticated && isConnected) {
      const getMaestroPermissions = async () => {
          const result = await getMaestroAuth(getAccessTokenSilently);
          if (result) {
            setMaestroPermissions(result.permissions);
          }
      };

      getMaestroPermissions();
    }
  }, [isConnected, isAuthenticated, getAccessTokenSilently]);

  if (isLoading) {
    return (
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 3, alignItems: 'center', textAlign: 'center' }}
      >
        <CircularProgress size={60} thickness={4} />
      </Box>
    );
  }
  if (error) {
    return (
      < Box
        component="main"
        sx={{ flexGrow: 1, p: 3, alignItems: 'center', textAlign: 'center' }
        }
      > {error.message}
      </Box >
    );
  }

  if (isAuthenticated) {
    if (customer_list.length > 0 && current_user !== null && current_broker !== null) {
      var customers = customer_list;
      if (customers[0].buyer_broker_id !== 'all' && current_user.company.includes('all')) {
        customers.unshift({ buyer_broker_id: 'all', bought_from_name: 'Show All' });
      }
      return (
        <>
          <ToastContainer />
          <Backdrop
            sx={{ color: '#fff', zIndex: 10000000 }}
            open={!!loading}
          >
            <CircularProgress color="inherit" />
          </Backdrop>

          <Dialog
            fullScreen
            open={openDialog}
            onClose={(event, reason) => {
              console.log('MUI Dialog onClose fired:', reason);

              if (reason === 'escapeKeyDown') {
                console.log('Ignoring ESC');
                return;
              }

              handleDialogClose(event, reason);
            }}
            TransitionComponent={Transition}
            onKeyDownCapture={(e) => {
              if (e.key === 'Escape') {
                console.log('ESC captured');
                e.stopPropagation();
              }
            }}
          >
            <AppBar sx={{ position: 'relative' }}>
              <Toolbar>
                <IconButton
                  edge="start"
                  color="inherit"
                  onClick={(event) => handleDialogClose(event)}
                  aria-label="close"
                >
                  <CloseIcon />
                </IconButton>
                <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                  {
                    dialogContent === 'user' ? 'User Management'
                      : dialogContent === 'commission' ? 'Commission Management'
                        : dialogContent === 'commissions-approval' ? 'Commission Approval'
                          : dialogContent === 'close-cycles' ? 'Close Cycles'
                            : dialogContent === 'vendors' ? 'Vendor Management'
                              : dialogContent === 'payee' ? 'Payout Configuration'
                                : dialogContent === 'ncaa' ? 'NCAA Report'
                                  : dialogContent === 'invoices' ? 'Invoice History'
                                    : dialogContent === 'payments' ? 'Payment History'
                                      : dialogContent === 'videos' ? 'Training Materials'
                                        : dialogContent === 'feature-request' ? 'Feature Request'
                                          : dialogContent === 'event-request' ? 'Event Request'
                                            : 'Content'
                  }
                </Typography>
              </Toolbar>
            </AppBar>
            <DialogContent>
              {current_user.admin && dialogContent === 'user' && <Users isConnected={isConnected} user={current_user} />}

              {current_user.client_admin && dialogContent === 'user' && <Users isConnected={isConnected} user={current_user} />}
              {current_user.admin && dialogContent === 'commission' && <Commissions isConnected={isConnected} user={current_user} />}

              {maestroPermissions.includes(`${process.env.REACT_APP_TENANT}:RevProVendor:Read`) && dialogContent === 'vendors' && <Vendors isConnected={isConnected} user={current_user} maestroPermissions={maestroPermissions} />}
              {maestroPermissions.includes(`${process.env.REACT_APP_TENANT}:Commissions:Cycles:Read`) && dialogContent === 'cycle-management' && <CycleManagement isConnected={isConnected} user={current_user} />}
              {maestroPermissions.includes(`${process.env.REACT_APP_TENANT}:Commissions:LineItems:Read`) && dialogContent === 'commissions-approval' && <CommissionsApproval isConnected={isConnected} user={current_user} maestroPermissions={maestroPermissions} />}
              {maestroPermissions.includes(`${process.env.REACT_APP_TENANT}:Commissions:LineItems:Read:Approval`) && dialogContent === 'close-cycles' && <CloseCycles isConnected={isConnected} user={current_user} maestroPermissions={maestroPermissions} />}
              {dialogContent === 'invoice-report' && <InvoiceReport isConnected={isConnected} current_broker={current_broker} maestroPermissions={maestroPermissions} />}

              {dialogContent === 'videos' && <VideoPlayer />}
              {dialogContent === 'feature-request' && <FeatureRequest current_broker={current_broker} current_user={current_user} onClose={handleDialogClose} />}
              {dialogContent === 'event-request' && <EventRequest current_broker={current_broker} current_user={current_user} onClose={handleDialogClose} />}
              {
                current_broker !== 'all' &&
                dialogContent === 'payee' &&
                <iframe
                  src={`${process.env.REACT_APP_TIPALTI_BASEURL}/payeedashboard/home?${tipaltiQueryString}`}
                  title="Payout Configuration"
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                />
              }
              {
                current_broker !== 'all' &&
                dialogContent === 'invoices' &&
                <iframe
                  src={`${process.env.REACT_APP_TIPALTI_BASEURL}/PayeeDashboard/Invoices?${tipaltiQueryString}`}
                  title="Invoice History"
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                />
              }
              {
                current_broker !== 'all' &&
                dialogContent === 'payments' &&
                <iframe
                  src={`${process.env.REACT_APP_TIPALTI_BASEURL}/PayeeDashboard/PaymentsHistory?${tipaltiQueryString}`}
                  title="Payment History"
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                />
              }
              {
                dialogContent === 'ncaa' &&
                <iframe
                  src={`https://reports.vegastickets.com/`}
                  title="NCAA Report"
                  width="100%"
                  height="100%"
                  style={{ border: 'none' }}
                />
              }
            </DialogContent>
          </Dialog>

          {optInConfirmationDialog()}

          <Paper elevation={5}>
            <Grid container spacing={2} sx={{ flexGrow: 1 }} alignItems={'center'} alignContent={'center'}>
              <Grid item xs={12} sm={3} align={'left'}>
                <Box
                  component="img"
                  sx={{
                    height: 64,
                    width: 'auto',
                    maxWidth: '100%',
                    marginLeft: '20px'
                  }}
                  alt="Logo"
                  src='/images/logo.png'
                />
              </Grid>
              <Grid item xs={12} sm={9}>
                <Grid container sx={{ flexGrow: 1 }} alignItems={'center'} alignContent={'center'}>
                  <Grid item xs={10} md={11} align={'right'}>
                    {
                      customers.length > 1
                        ?
                        <Autocomplete
                          options={customers}
                          getOptionLabel={(option) => option.bought_from_name}
                          getOptionKey={(option) => option.buyer_broker_id}
                          defaultValue={customers[0]}
                          disableClearable={true}
                          onChange={(event, newValue) => {
                            setCurrentBroker(newValue.buyer_broker_id);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Vendor Search"
                            />
                          )}
                          size="small"
                          sx={{ width: '300px' }}
                        />
                        :
                        <Typography variant="h5" sx={{ color: process.env.REACT_APP_COLOR1 }}>{customers[0].bought_from_name}</Typography>
                    }
                  </Grid>
                  <Grid item xs={2} md={1} align={'center'}>
                    <Tooltip title="Account Info">
                      <IconButton onClick={handleClick} color='success' size='small'>
                        <AccountCircleRoundedIcon fontSize='large' />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      anchorEl={anchorEl}
                      id="account-menu"
                      open={open}
                      onClose={handleClose}
                      onClick={handleClose}
                      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                    >
                      <MenuItem disabled>{`${current_user.fname} ${current_user.lname}`}</MenuItem>
                      <MenuItem disabled sx={{ fontSize: '.75rem' }}>{`${current_user.email}`}</MenuItem>
                      <Divider />
                      {
                        current_broker !== 'all' && (current_user.admin || current_user.client_admin) &&
                        <>
                          <MenuItem onClick={() => handleDialogClickOpen('payee')}>
                            <ListItemIcon>
                              <AssuredWorkloadRoundedIcon fontSize="small" color='primary' />
                            </ListItemIcon>
                            Payout Configuration
                          </MenuItem>
                          <MenuItem onClick={() => handleDialogClickOpen('payments')}>
                            <ListItemIcon>
                              <PaymentsRoundedIcon fontSize="small" color='primary' />
                            </ListItemIcon>
                            Payment History
                          </MenuItem>
                          <Divider />
                        </>
                      }
                      {
                        optInMenu()
                      }
                      {
                        current_user.admin || current_user.client_admin
                          ?
                          <>
                            <MenuItem onClick={() => handleModalNavigate('/users')}>
                              <ListItemIcon>
                                <GroupsRoundedIcon fontSize="small" color='primary' />
                              </ListItemIcon>
                              User Management
                            </MenuItem>
                                <MenuItem onClick={() => handleModalNavigate('/commissions')}>
                                  <ListItemIcon>
                                    <CurrencyExchangeRoundedIcon fontSize="small" color='primary' />
                                  </ListItemIcon>
                                  Commission Management (V1)
                                </MenuItem>
                                <Divider />
                            {maestroPermissions.includes(`${process.env.REACT_APP_TENANT}:RevProVendor:Read`) && (
                            <>
                              <MenuItem onClick={() => handleModalNavigate('/vendors')}>
                                <ListItemIcon>
                                  <PermContactCalendar fontSize="small" color='primary' />
                                </ListItemIcon>
                                Vendor Management
                              </MenuItem>
                            </>
                            )}
                            {maestroPermissions.includes(`${process.env.REACT_APP_TENANT}:Commissions:Cycles:Read`) && (
                              <>
                                <MenuItem onClick={() => handleModalNavigate('/cycle-management')}>
                                  <ListItemIcon>
                                    <EventAvailable fontSize="small" color='primary' />
                                  </ListItemIcon>
                                  Cycle Management
                                </MenuItem>
                              </>
                              )}
                            {maestroPermissions.includes(`${process.env.REACT_APP_TENANT}:Commissions:LineItems:Read`) && (
                              <>
                                <MenuItem onClick={() => handleModalNavigate('/commissions-approval')}>
                                  <ListItemIcon>
                                    <PublishedWithChangesOutlined fontSize="small" color='primary' />
                                  </ListItemIcon>
                                  Commission Approval
                                </MenuItem>
                              </>
                            )}
                            {maestroPermissions.includes(`${process.env.REACT_APP_TENANT}:Commissions:LineItems:Read`) && (
                            <>
                              <MenuItem onClick={() => handleModalNavigate('/close-cycles')}>
                                <ListItemIcon>
                                  <EventBusy fontSize="small" color='primary' />
                                </ListItemIcon>
                                Close Cycles
                              </MenuItem>
                            </>
                            )}
                            <>
                              <MenuItem onClick={() => handleModalNavigate('/invoice-report')}>
                                <ListItemIcon>
                                  <Summarize fontSize="small" color='primary' />
                                </ListItemIcon>
                                Invoice Report
                              </MenuItem>
                            </>
                          </>
                          :
                          <></>
                      }
                      {
                        current_user.enableNcaaReport && (
                        <>
                          <Divider />
                          <MenuItem onClick={() => handleDialogClickOpen('ncaa')}>
                            <ListItemIcon>
                              <SportsBasketball fontSize="small" color='primary' />
                            </ListItemIcon>
                            NCAA Report
                          </MenuItem>
                      </>
                        )
                      }
                      <Divider/>
                      <MenuItem onClick={() => handleModalNavigate('/training')}>
                        <ListItemIcon>
                          <PlayCircleFilledIcon fontSize="small" color='success' />
                        </ListItemIcon>
                        Training Materials
                      </MenuItem>
                      <MenuItem onClick={() => handleModalNavigate('/feature-request')}>
                        <ListItemIcon>
                          <FeedbackIcon fontSize="small" color='info' />
                        </ListItemIcon>
                        Feature Request
                      </MenuItem>
                      <MenuItem onClick={() => handleModalNavigate('/event-request')}>
                        <ListItemIcon>
                          <EventIcon fontSize="small" color='warning' />
                        </ListItemIcon>
                        Event Request
                      </MenuItem>
                      <Divider />
                      <MenuItem onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}>
                        <ListItemIcon>
                          <LogoutRoundedIcon fontSize="small" color='error' />
                        </ListItemIcon>
                        LogOut
                      </MenuItem>
                    </Menu>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <TabContext value={ activeRouteTabValue }>
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <TabList value={activeRouteTabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
                  {mainRoutes[0].children
                    ?.filter(route => !route.isModal)
                    .map((route) => {
                      const tabVal = route.index ? '/dashboard' : `/${route.path}`;
                      return (
                        <Tab key={route.path || 'index'} label={route.label} value={tabVal} />
                      );
                    })
                  }
                </TabList>
              </Box>
              <Box sx={{ p: 3 }}>
                <Routes location={backgroundTabLocation}>
                  {mainRoutes[0].children
                    ?.filter(route => !route.isModal)
                    .map((route) => (
                      <Route
                      key={route.path || 'index'}
                      index={route.index}
                      path={route.path}
                      element={
                        <route.element 
                          isConnected={isConnected} 
                          current_broker={current_broker} 
                          current_user={current_user}
                          tipaltiQueryString={tipaltiQueryString}
                          openEventRequestDialog={openEventRequestDialog} />}
                    />
                  ))}
                </Routes>
              </Box>
              { isCurrentlyModal && (
                <Dialog 
                  open={true} 
                  onClose={(event, reason) => {
                    if (reason === 'escapeKeyDown') {
                      return;
                    }

                    handleDialogClose();
                  }}
                  fullScreen
                >
                  <AppBar sx={{ position: 'relative' }}>
                    <Toolbar>
                          <IconButton
                            edge="start"
                            color="inherit"
                            onClick={handleDialogClose}
                            aria-label="close"
                          >
                            <CloseIcon />
                          </IconButton>
                          <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
                          {['vendors', 'cycle-management', 'commissions-approval', 'close-cycles'].includes(currentRoute?.path)
                            ? `${dynamicModalRouteLabel} - ${tenant}`
                            : dynamicModalRouteLabel}
                        </Typography>
                        </Toolbar>
                  </AppBar>
                  <DialogContent sx={{ bgcolor: '#f5f5f5', p: 4 }}>
                    <Outlet context={{ isConnected, maestroPermissions, current_broker, current_user, tipaltiQueryString, openEventRequestDialog }} /> 
                  </DialogContent>
                </Dialog>
              )}
            </TabContext>
          </Paper >
        </>
      );
    } else {
      return (
        <Box
          component="main"
          sx={{ flexGrow: 1, p: 3, alignItems: 'center', textAlign: 'center' }}
        >
          <CircularProgress size={60} thickness={4} />
        </Box>
      );
    }
  } else {
    loginWithRedirect();
  }
}

export default Landing;